import React, { useEffect, useState, useRef } from "react";
import { Box, IconButton, Typography, makeStyles } from "@material-ui/core";
import BackIcon from "@material-ui/icons/ArrowBack";
import StyledDrawer from "../StyledDrawer";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import EditDialogActions from "../../common/EditDialogActions";
import Form from "../../common/Form";
import UnsavedChangesDialog from "../../dialogs/UnsavedChangesDialog";
import API from "../../../utils/axios";
import { useSnackbar } from "notistack";

const useStyles = makeStyles(theme => ({
  header: {
    height: 80,
    borderBottom: "1px solid #eee",
    boxSizing: "border-box"
  },
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500
  },
  subtitle: {
    lineHeight: "1.1875rem",
    fontWeight: 500
  }
}));

function TagsEditDialog({
  open,
  currentTag,
  setCurrentAction,
  tags,
  setTags,
  saveDisabled,
  setSaveDisabled
}) {
  const classes = useStyles();
  const initialValues = { tagName: "" };
  const initialSelectValues = { type: "VEHICLE" };

  const snackbarKey = useRef();

  const [inputValues, setInputValues] = useState({ ...initialValues });
  const [selectValues, setSelectValues] = useState({ ...initialSelectValues });
  const [errors, setErrors] = useState({ ...initialValues });
  const [changesDialog, setChangesDialog] = useState(true);

  const [loading, setLoading] = useState(false);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleChange = e => {
    if (e.target.value.includes(";")) {
      if (snackbarKey.current) closeSnackbar(snackbarKey.current);
      snackbarKey.current = enqueueSnackbar(
        "Not allowed to use semicolon ';' as this is the tag delimiter character."
      );
      return;
    }
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
    if (e.target.value.trim()) setSaveDisabled(false);
  };

  const handleSelect = e => {
    setSelectValues({ ...selectValues, [e.target.name]: e.target.value });
    if (inputValues.tagName.trim()) setSaveDisabled(false);
  };

  // Close edit panel
  const handleClose = () => {
    setSaveDisabled(true);
    setCurrentAction("overview");
  };

  const handleSave = () => {
    if (!inputValues.tagName.trim()) {
      setErrors({ ...errors, tagName: "Please fill in this field" });
      return;
    }

    setLoading(true);
    if (currentTag) {
      const body = {
        name: inputValues.tagName,
        type: selectValues.type
      };
      API.patch(`/v1/tags/${currentTag.id}`, body)
        .then(res => {
          // setTags({})
          setTags(
            tags.map(tag =>
              tag.id === currentTag.id ? { ...tag, ...body } : tag
            )
          );
          handleClose();
          setLoading(false);
          enqueueSnackbar(`Tag "${inputValues.tagName}" edited.`);
        })
        .catch(err => {
          if (err && err.status === 400 && err.data && err.data.errors[0]) {
            let field = err.data.errors[0].field;
            setErrors({
              [field]: err.data.errors[0].defaultMessage
            });
          } else {
            enqueueSnackbar("Something went wrong");
          }
          setLoading(false);
        });
    } else {
      API.post("/v1/tags", {
        name: inputValues.tagName,
        type: selectValues.type
      })
        .then(res => {
          // setTags({})
          setTags([res.data, ...tags]);
          handleClose();
          setLoading(false);
          enqueueSnackbar(`Tag "${inputValues.tagName}" added.`);
        })
        .catch(err => {
          if (err && err.status === 400 && err.data && err.data.errors[0]) {
            let field = err.data.errors[0].field;
            setErrors({
              [field]: err.data.errors[0].defaultMessage
            });
          } else {
            enqueueSnackbar("Something went wrong");
          }
          setLoading(false);
        });
    }
  };

  // Warn about unsaved changes before closing
  const warnClose = saveDisabled
    ? handleClose
    : () =>
        setChangesDialog({
          visible: true,
          action: handleClose
        });

  useEffect(() => {
    if (currentTag) {
      setInputValues({ tagName: currentTag.name });
      setSelectValues({ type: currentTag.type.toUpperCase() });
    }
  }, [currentTag]);

  return (
    <>
      <StyledDrawer
        open={open}
        noAnim
        handleClose={warnClose}
        loading={loading}
      >
        {/* Header */}
        <Box
          px={3}
          className={classes.header}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
        >
          <OptimizedTooltip placement="bottom" title="Close">
            <Box ml={-1} mr={2}>
              <IconButton onClick={warnClose} data-cy="tag-closeEdit">
                <BackIcon />
              </IconButton>
            </Box>
          </OptimizedTooltip>
          <Box alignSelf="center">
            <Typography variant="h5" className={classes.title}>
              {currentTag && currentTag.id ? "Edit tag" : "Add tag"}
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <Box pt={4} pb={1} pl={3} pr={5} mb={1.5}>
          {/* Name */}
          <Box display="flex" alignItems="center" mb={2}>
            <Typography className={classes.subtitle}>Tag details</Typography>
          </Box>

          {/* Form */}
          <Form
            data={[
              [
                {
                  renderInputField: true,
                  label: "Tag name",
                  name: "tagName",
                  type: "text",
                  autoFocus: true,
                  dataCy: "tag-name",
                  onPaste: e => {
                    if (e.clipboardData.getData("Text")?.includes(";")) {
                      if (snackbarKey.current)
                        closeSnackbar(snackbarKey.current);
                      snackbarKey.current = enqueueSnackbar(
                        "Not allowed to use semicolon ';' as this is the tag delimiter character."
                      );
                      e.preventDefault();
                    }
                  }
                }
              ],
              [
                {
                  labelId: "tag-type",
                  selectId: "tab-type-select",
                  label: "Type",
                  name: "type",
                  choose: false,
                  disabled: Boolean(currentTag),
                  options: [
                    ["VEHICLE", "Vehicle"],
                    ["DRIVER", "Driver"]
                  ],
                  shrink: true,
                  dataCy: "tag-select-type",
                  onChange: e => handleSelect(e),
                  componentType: "select"
                }
              ]
            ]}
            inputValues={inputValues}
            selectValues={selectValues}
            errorValues={errors}
            handleChange={handleChange}
          />
        </Box>

        {/* Actions */}
        <EditDialogActions
          section="tags"
          handleClose={warnClose}
          handleClick={handleSave}
          disabled={saveDisabled}
        />
        <UnsavedChangesDialog
          open={changesDialog.visible}
          handleClose={() => {
            setChangesDialog({ visible: false, action: () => {} });
          }}
          action={() => {
            changesDialog.action();
            setSaveDisabled(true);
          }}
        />
      </StyledDrawer>
    </>
  );
}

export default TagsEditDialog;
